import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FileText, FilePdf, ArrowSquareOut, CaretRight, Spinner, Check, X, Gavel, Tag, UserPlus, ArrowsClockwise, CalendarPlus, SkipForward, LinkSimple, SealCheck, MinusCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { centsToUSD, factLabel, trustTone, toneClasses, fmtDate, prettyFact, formatAPUValue, trustLabel, eventLabel, errorLabel } from "@/lib/morning";

const Badge = ({ tone, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${toneClasses(tone)}`}>{children}</span>
);
const Section = ({ title, count, defaultOpen = false, children }) => (
  <details open={defaultOpen} className="border-t border-border py-3">
    <summary className="cursor-pointer list-none flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
      <span>{title}{count != null ? ` (${count})` : ""}</span><CaretRight size={12} />
    </summary>
    <div className="mt-3 space-y-3">{children}</div>
  </details>
);
const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 text-sm"><span className="text-muted-foreground shrink-0">{k}</span><span className="text-right font-medium break-words">{v}</span></div>
);

export const ProvenanceDrawer = ({ entity, hid, factLabels, factTypes = [], assets = [], onClose, onOpenApu, onAction, isMobile }) => {
  const [prov, setProv] = useState(null);
  const [docApus, setDocApus] = useState(null);
  const [events, setEvents] = useState(null);
  const [attrPropertyId, setAttrPropertyId] = useState("");
  const [attrShowAllProperties, setAttrShowAllProperties] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdf, setPdf] = useState({ open: false, url: null, loading: false, name: "", docId: null });
  const [modal, setModal] = useState(null);     // {kind, ctx}
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (entity?.type === "apu") {
      const r = await api.get(`/apus/${entity.id}/provenance?household_id=${hid}`).catch(() => null);
      if (r) setProv(r.data);
    }
    onAction && onAction();
  }, [entity, hid, onAction]);

  useEffect(() => {
    if (!entity) { setProv(null); setDocApus(null); setEvents(null); return; }
    setAttrPropertyId(entity.type === "attribution" ? (entity.data?.candidate_property_id || "") : "");
    setAttrShowAllProperties(false);
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        if (entity.type === "apu") {
          const r = await api.get(`/apus/${entity.id}/provenance?household_id=${hid}`);
          if (!cancel) setProv(r.data);
        } else if (entity.type === "document") {
          const r = await api.get(`/apus?household_id=${hid}&source.document_id=${entity.id}`);
          if (!cancel) setDocApus(r.data);
        } else if (entity.type === "expected_statement") {
          const r = await api.get(`/reconciliation-events?household_id=${hid}`);
          if (!cancel) setEvents((r.data || []).filter((e) => e.expected_statement_id === entity.id));
        }
      } catch (e) { /* empty */ }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, [entity, hid]);

  const openPdf = useCallback(async (docId, name) => {
    setPdf({ open: true, url: null, loading: true, name: name || "Document", docId });
    try {
      const r = await api.get(`/documents/${docId}/file?household_id=${hid}`, { responseType: "blob" });
      setPdf({ open: true, url: URL.createObjectURL(r.data), loading: false, name: name || "Document", docId });
    } catch (e) { setPdf({ open: true, url: null, loading: false, name: name || "Document", docId }); }
  }, [hid]);
  useEffect(() => () => { if (pdf.url) URL.revokeObjectURL(pdf.url); }, [pdf.url]);

  // ── action runner (optimistic-ish: act → toast → refetch; rollback on error) ──
  const run = async (label, fn, after) => {
    setBusy(true);
    const t = toast.loading(`${label}…`);
    try {
      const res = await fn();
      toast.success(label + " done", { id: t });
      setModal(null);
      await reload();
      after && after(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Action failed";
      toast.error(`${label} failed: ${msg}`, { id: t });
    } finally { setBusy(false); }
  };

  const actions = {
    confirmRec: (rid) => run("Confirm match", () => api.post(`/reconciliations/${rid}/confirm?household_id=${hid}`)),
    rejectRec: (rid) => run("Reject match", () => api.post(`/reconciliations/${rid}/reject?household_id=${hid}`)),
    acceptAttr: (aid, propertyId) => run("Accept attribution", () => api.post(`/attributions/${aid}/accept?household_id=${hid}`, { property_id: propertyId }), () => onClose && onClose()),
    rejectAttr: (aid) => run("Reject attribution", () => api.post(`/attributions/${aid}/reject?household_id=${hid}`), () => onClose && onClose()),
    disputeRec: (rid) => setModal({ kind: "dispute_rec", rid }),
    disputeApu: () => setModal({ kind: "dispute_apu" }),
    classify: () => setModal({ kind: "classify" }),
    rejectApu: () => setModal({ kind: "reject_apu" }),
  };

  const title = { apu: "Receipt", document: "Document", expected_statement: "Expected statement", adapter_error: "Needs your call", attribution: "Attribution" }[entity?.type] || "Details";
  const isMissingOwner = entity?.type === "adapter_error" && entity?.data?.error_type === "missing_owner";

  return (
    <>
      <Sheet open={!!entity} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side={isMobile ? "bottom" : "right"} className={isMobile ? "h-[88vh] rounded-t-2xl p-0" : "w-full sm:max-w-xl p-0"} data-testid="provenance-drawer">
          <SheetHeader className="px-6 pt-6 pb-3 border-b border-border text-left">
            <SheetTitle className="font-serif text-2xl">{title}</SheetTitle>
            <SheetDescription className="sr-only">The receipt for this number and what you can do about it</SheetDescription>
          </SheetHeader>
          <ScrollArea className={isMobile ? "h-[calc(88vh-130px)]" : "h-[calc(100vh-130px)]"}>
            <div className="px-6 py-4">
              {loading && <div className="py-12 flex items-center justify-center text-muted-foreground"><Spinner size={20} className="animate-spin mr-2" /> Loading…</div>}

              {!loading && entity?.type === "apu" && prov && <ApuBody prov={prov} factLabels={factLabels} openPdf={openPdf} actions={actions} />}

              {!loading && entity?.type === "document" && (
                <div className="space-y-4" data-testid="drawer-document">
                  <Row k="Type" v={prettyFact(entity.data?.type)} />
                  <Row k="Source" v={entity.data?.source_kind || "—"} />
                  <Row k="Received" v={fmtDate(entity.data?.received_at)} />
                  <Row k="SHA-256" v={<span className="font-mono text-[10px] break-all">{entity.data?.sha256 || "—"}</span>} />
                  <button onClick={() => openPdf(entity.id, entity.data?.original_filename)} data-testid="drawer-doc-view-pdf" className="inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--accent))] hover:underline"><FilePdf size={16} /> View {entity.data?.original_filename || "file"}</button>
                  <Section title="Numbers from this document" count={docApus?.length} defaultOpen>
                    {(docApus || []).map((a) => (
                      <button key={a.id} data-testid={`drawer-doc-apu-${a.id}`} onClick={() => onOpenApu(a.id)} className="w-full text-left flex items-center justify-between gap-2 py-2 border-b border-border/60 last:border-0 hover:bg-[hsl(36_30%_96%)] -mx-2 px-2 rounded">
                        <span className="text-sm"><span className="font-semibold tabular-nums">{formatAPUValue(a.value, a.unit_of_value, true)}</span><span className="text-muted-foreground"> · {factLabel(a.fact_type, factLabels)}</span></span>
                        <Badge tone={trustTone(a.trust_state)}>{trustLabel(a.trust_state)}</Badge>
                      </button>
                    ))}
                    {docApus && docApus.length === 0 && <p className="text-sm text-muted-foreground">No numbers derived.</p>}
                  </Section>
                </div>
              )}

              {!loading && entity?.type === "expected_statement" && (
                <div className="space-y-4" data-testid="drawer-expected-statement">
                  <Row k="Status" v={<Badge tone={entity.data?.status === "overdue" ? "destructive" : "muted"}>{entity.data?.status}</Badge>} />
                  <Row k="Due date" v={fmtDate(entity.data?.due_date)} />
                  <Row k="Days overdue" v={entity.data?.days_overdue ?? "—"} />
                  <Row k="Period" v={`${fmtDate(entity.data?.period?.start)} → ${fmtDate(entity.data?.period?.end)}`} />
                  {entity.data?.received_document_id && (
                    <button onClick={() => openPdf(entity.data.received_document_id, "Received statement")} className="inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--accent))] hover:underline"><FilePdf size={16} /> View received document</button>
                  )}
                  <Section title="Event chain" count={events?.length} defaultOpen>
                    {(events || []).map((e) => (
                      <div key={e.id} className="text-sm border-b border-border/60 last:border-0 pb-2"><p className="font-medium">{eventLabel(e.event_type || e.triggered_by)}</p><p className="text-xs text-muted-foreground">{trustLabel(e.from_state)} → {trustLabel(e.to_state)} · {fmtDate(e.decided_at)}</p></div>
                    ))}
                    {events && events.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
                  </Section>
                </div>
              )}

              {!loading && entity?.type === "adapter_error" && (
                <div className="space-y-4" data-testid="drawer-adapter-error">
                  <Badge tone="destructive" testid="drawer-error-type">{errorLabel(entity.data?.error_type)}</Badge>
                  {(entity.data?.details?.owner_code) && (
                    <div className="bg-[hsl(var(--destructive)/0.08)] border border-[hsl(var(--destructive)/0.3)] rounded-lg p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Owner code</p>
                      <p className="font-serif text-xl" data-testid="drawer-error-owner-code">{entity.data?.details?.owner_code}</p>
                    </div>
                  )}
                  <Row k="Bundle / statement" v={<span className="font-mono text-[10px] break-all">{entity.data?.details?.statement_id || entity.data?.bundle_id || "—"}</span>} />
                  <Row k="Operator" v={entity.data?.details?.operator || "—"} />
                  {entity.data?.details?.delta_cents != null && <Row k="Cross-foot delta" v={centsToUSD(entity.data.details.delta_cents, true)} />}
                  {entity.data?.resolved && <Badge tone="success">resolved by {entity.data?.resolved_by}</Badge>}
                  <Section title="Breakdown" defaultOpen>
                    <pre className="text-[11px] bg-muted rounded-lg p-3 overflow-x-auto">{JSON.stringify(entity.data?.details?.breakdown || entity.data?.details || {}, null, 2)}</pre>
                  </Section>
                </div>
              )}

              {!loading && entity?.type === "attribution" && (
                <AttributionBody attr={entity.data} factLabels={factLabels} assets={assets}
                  value={attrPropertyId} onValueChange={setAttrPropertyId} showAllProperties={attrShowAllProperties} />
              )}
            </div>
          </ScrollArea>

          {/* ── footer action bar (per entity type) ── */}
          <div className="px-6 py-3 border-t border-border bg-muted/40 flex flex-wrap gap-2">
            {entity?.type === "apu" && prov && (
              <>
                <Button size="sm" variant="outline" data-testid="action-dispute-apu" disabled={busy} onClick={actions.disputeApu}><Gavel size={14} className="mr-1.5" /> Dispute</Button>
                <Button size="sm" variant="outline" data-testid="action-classify-apu" disabled={busy} onClick={actions.classify}><Tag size={14} className="mr-1.5" /> Classify</Button>
                <Button size="sm" variant="outline" data-testid="action-reject-apu" disabled={busy} onClick={actions.rejectApu}><MinusCircle size={14} className="mr-1.5" /> Not a value</Button>
              </>
            )}
            {entity?.type === "expected_statement" && (
              <>
                <Button size="sm" variant="outline" data-testid="action-extend-es" disabled={busy} onClick={() => setModal({ kind: "extend_es" })}><CalendarPlus size={14} className="mr-1.5" /> Extend</Button>
                <Button size="sm" variant="outline" data-testid="action-skip-es" disabled={busy} onClick={() => setModal({ kind: "skip_es" })}><SkipForward size={14} className="mr-1.5" /> Skip</Button>
                <Button size="sm" variant="outline" data-testid="action-link-doc" disabled={busy} onClick={() => setModal({ kind: "link_doc" })}><LinkSimple size={14} className="mr-1.5" /> Link document</Button>
              </>
            )}
            {entity?.type === "adapter_error" && (
              <>
                {isMissingOwner && <Button size="sm" data-testid="action-assign-owner" disabled={busy} onClick={() => setModal({ kind: "assign_owner" })}><UserPlus size={14} className="mr-1.5" /> Assign owner</Button>}
                <Button size="sm" variant="outline" data-testid="action-retry-bundle" disabled={busy} onClick={() => actions && setModal({ kind: "retry" })}><ArrowsClockwise size={14} className="mr-1.5" /> Retry conversion</Button>
                {!entity?.data?.resolved && <Button size="sm" variant="outline" data-testid="action-resolve-error" disabled={busy} onClick={() => setModal({ kind: "resolve_error" })}><SealCheck size={14} className="mr-1.5" /> Resolve</Button>}
              </>
            )}
            {entity?.type === "attribution" && (
              <>
                <Button size="sm" data-testid={`attr-accept-${entity.id}`} disabled={busy || !attrPropertyId} onClick={() => actions.acceptAttr(entity.id, attrPropertyId)}><Check size={14} className="mr-1.5" /> Accept attribution</Button>
                <Button size="sm" variant="outline" data-testid={`attr-reassign-${entity.id}`} disabled={busy} onClick={() => setAttrShowAllProperties(true)}><ArrowsClockwise size={14} className="mr-1.5" /> Reassign property</Button>
                <Button size="sm" variant="ghost" className="ml-auto" data-testid={`attr-reject-${entity.id}`} disabled={busy} onClick={() => actions.rejectAttr(entity.id)}><X size={14} className="mr-1.5" /> Reject</Button>
              </>
            )}
            {(!entity || entity?.type === "document") && <p className="text-[11px] text-muted-foreground">All operator actions emit an audit event stamped with who acted.</p>}
          </div>
        </SheetContent>
      </Sheet>

      {/* PDF preview */}
      <Dialog open={pdf.open} onOpenChange={(o) => !o && setPdf({ open: false, url: null, loading: false, name: "", docId: null })}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col" data-testid="pdf-preview-dialog">
          <DialogHeader className="px-4 py-3 border-b border-border"><DialogTitle className="text-sm flex items-center gap-2"><FileText size={16} /> {pdf.name}</DialogTitle><DialogDescription className="sr-only">Document preview</DialogDescription></DialogHeader>
          <div className="flex-1 min-h-0">
            {pdf.loading && <div className="h-full flex items-center justify-center text-muted-foreground"><Spinner size={20} className="animate-spin mr-2" /> Loading…</div>}
            {!pdf.loading && pdf.url && <embed src={pdf.url} type="application/pdf" className="w-full h-full" data-testid="pdf-embed" />}
            {!pdf.loading && !pdf.url && pdf.docId && (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2"><p>Preview unavailable.</p><a href={`${api.defaults.baseURL}/documents/${pdf.docId}/file?household_id=${hid}&download=1`} className="inline-flex items-center gap-1 text-[hsl(var(--accent))] hover:underline"><ArrowSquareOut size={14} /> Open in new tab</a></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ActionModal modal={modal} setModal={setModal} busy={busy} run={run} api={api} hid={hid}
        entity={entity} factTypes={factTypes} actions={actions} />
    </>
  );
};

const quoteSnippet = (text) => (
  <blockquote className="rounded bg-background border-l-2 border-[#b08d57] px-3 py-2 text-sm text-foreground">
    {text || <span className="text-muted-foreground">No snippet extracted.</span>}
  </blockquote>
);

const evidenceParts = (signals = {}) => {
  const delta = signals.amount_delta_cents;
  const days = signals.days_apart;
  const count = signals.corroborating_comms_count || 0;
  const amount = delta === 0
    ? "Exact amount match"
    : `${centsToUSD(Math.abs(delta || 0), true)} off`;
  const dayText = days === 0
    ? "same day"
    : `${Math.abs(days || 0)} day${Math.abs(days || 0) === 1 ? "" : "s"} apart`;
  return { amount, dayText, count };
};

const AttributionBody = ({ attr, factLabels, assets, value, onValueChange, showAllProperties = false }) => {
  const bank = attr?.enriched?.bank_apu || {};
  const comms = attr?.enriched?.comms_apus || [];
  const candidate = attr?.enriched?.candidate_property || {};
  const ties = attr?.enriched?.tie_candidates || [];
  const messages = attr?.enriched?.citing_messages || [];
  const realEstate = (assets || []).filter((a) => a.asset_type === "real_estate");
  const selectable = showAllProperties ? realEstate : ties.length
    ? ties.map((t) => t.property).filter(Boolean)
    : candidate.id ? [candidate] : realEstate;
  const commByDocument = Object.fromEntries(comms.map((a) => [a.source?.document_id, a]));
  const evidence = evidenceParts(attr?.signals || {});
  const mappingOnly = !!attr?.signals?.mapping_id;
  const mapping = attr?.mapping || {};
  return (
    <div className="space-y-4" data-testid="drawer-attribution">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-serif text-4xl tabular-nums">{formatAPUValue(bank.value, bank.unit_of_value || "USD_cents", true)}</span>
        <Badge tone={attr.state === "suggested" ? "warning" : attr.state === "accepted" ? "success" : attr.state === "rejected" ? "destructive" : "muted"}>{attr.state}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">{factLabel(bank.fact_type, factLabels)} · {fmtDate(bank.period?.start)}</p>
      {mappingOnly ? (
        <div data-testid="attr-mapping-provenance" className="text-sm border border-border rounded-lg p-3 bg-muted/40">
          <p className="font-semibold">Operator decision</p>
          <p className="text-muted-foreground">{[mapping.decided_by, mapping.source, prettyFact(mapping.kind)].filter(Boolean).join(" · ")}</p>
          {mapping.merchant_key && <p className="text-xs text-muted-foreground mt-1">{mapping.merchant_key}</p>}
        </div>
      ) : (
        <p data-testid="attr-evidence-line" className="text-sm text-muted-foreground">
          <strong className="text-foreground">{evidence.amount}</strong>
          <span> · </span>
          <strong className="text-foreground">{evidence.dayText}</strong>
          <span> · corroborated by </span>
          <strong className="text-foreground">{evidence.count} message{evidence.count === 1 ? "" : "s"}</strong>
        </p>
      )}

      <div className="border border-border rounded-lg p-3 space-y-3">
        <Row k="Confidence" v={`${((attr.confidence || 0) * 100).toFixed(0)}%`} />
        {selectable.length > 1 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Property</p>
            <Select value={value} onValueChange={onValueChange}>
              <SelectTrigger data-testid="attr-property-select"><SelectValue placeholder="Choose property" /></SelectTrigger>
              <SelectContent>
                {selectable.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.address || p.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <Row k="Property" v={candidate.name || candidate.address || attr.candidate_property_id || "Needs review"} />
        )}
        <Row k="Bank APU" v={<span className="font-mono text-[10px] break-all">{attr.bank_apu_id}</span>} />
      </div>

      <Section title="Citing messages" count={messages.length} defaultOpen>
        {messages.map((m, i) => {
          const comm = commByDocument[m.document_id] || comms[i] || {};
          return (
          <div key={m.id} className="text-sm border border-border rounded-lg p-3">
            <p className="font-semibold">{m.subject || m.id}</p>
            <p className="text-xs text-muted-foreground mb-3">{[m.counterparty_name || "counterparty", m.channel, fmtDate(m.sent_at)].filter(Boolean).join(" · ")}</p>
            {quoteSnippet(comm.source?.raw_text)}
          </div>
          );
        })}
        {messages.length > 0 && <p className="text-xs italic text-muted-foreground">Full message body isn't stored — showing the extracted snippet.</p>}
        {messages.length === 0 && <p className="text-sm text-muted-foreground">No message details found.</p>}
      </Section>

      <Section title="Corroborating comms APUs" count={comms.length}>
        {comms.map((a) => (
          <div key={a.id} className="text-sm border-b border-border/60 last:border-0 pb-2">
            <p className="font-semibold tabular-nums">{formatAPUValue(a.value, a.unit_of_value || "USD_cents", true)}</p>
            <p className="text-xs text-muted-foreground">{factLabel(a.fact_type, factLabels)} · {fmtDate(a.period?.start)}</p>
          </div>
        ))}
      </Section>

      {attr.signals && (
        <Section title="Signals" defaultOpen>
          <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground">
            {Object.entries(attr.signals).map(([key, v]) => <span key={key}>{prettyFact(key)}: <span className="font-medium text-foreground">{typeof v === "number" ? v.toFixed(2) : String(v)}</span></span>)}
          </div>
        </Section>
      )}
    </div>
  );
};

// ── Action modals ──
const ActionModal = ({ modal, setModal, busy, run, api, hid, entity, factTypes }) => {
  const [reason, setReason] = useState("");
  const [fact, setFact] = useState("");
  const [owner, setOwner] = useState("");
  const [legal, setLegal] = useState("");
  const [due, setDue] = useState("");
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [rejectReason, setRejectReason] = useState("not_a_value");
  const apuId = entity?.type === "apu" ? entity.id : null;
  const esId = entity?.type === "expected_statement" ? entity.id : null;
  const stmtId = entity?.data?.details?.statement_id || entity?.data?.bundle_id;

  useEffect(() => { setReason(""); setFact(""); setOwner(""); setLegal(""); setDue(""); setDocId(""); setRejectReason("not_a_value"); }, [modal?.kind]);
  useEffect(() => {
    if (modal?.kind === "link_doc") api.get(`/documents?household_id=${hid}`).then((r) => setDocs(r.data || [])).catch(() => {});
    if (modal?.kind === "assign_owner") setOwner("");
  }, [modal?.kind, hid]);

  if (!modal) return null;
  const close = () => setModal(null);
  const k = modal.kind;

  const submit = {
    dispute_apu: () => run("Dispute this number", () => api.post(`/apus/${apuId}/dispute?household_id=${hid}`, { reason })),
    dispute_rec: () => run("Dispute match", () => api.post(`/reconciliations/${modal.rid}/reject?household_id=${hid}`, { reason })),
    classify: () => run("Classify", () => api.post(`/apus/${apuId}/classify?household_id=${hid}`, { new_fact_type: fact, notes: reason || null })),
    assign_owner: () => run("Assign owner code", () => api.post(`/owner-code-mappings?household_id=${hid}`, { owner_code: entity?.data?.details?.owner_code, owner, entity_legal_name: legal, notes: reason || null }), () => setModal({ kind: "retry_offer" })),
    retry: () => run("Retry conversion", () => api.post(`/mineral/adapter/retry-bundle/${stmtId}?household_id=${hid}`)),
    retry_offer: () => run("Retry conversion", () => api.post(`/mineral/adapter/retry-bundle/${stmtId}?household_id=${hid}`)),
    extend_es: () => run("Extend", () => api.post(`/expected_statements/${esId}/extend?household_id=${hid}`, { new_due_date: due, reason })),
    skip_es: () => run("Skip", () => api.post(`/expected_statements/${esId}/skip?household_id=${hid}`, { reason })),
    link_doc: () => run("Link document", () => api.post(`/expected_statements/${esId}/link_document?household_id=${hid}`, { document_id: docId })),
    resolve_error: () => run("Resolve", () => api.post(`/adapter-errors/${entity.id}/resolve?household_id=${hid}`, { notes: reason })),
    reject_apu: () => run("Reject this value", () => api.post(`/apus/${apuId}/reject?household_id=${hid}`, { reason: rejectReason, notes: reason || null })),
  };

  const cfg = {
    dispute_apu: { title: "Dispute this number", needReason: true },
    dispute_rec: { title: "Dispute this match", needReason: true },
    classify: { title: "Reclassify this number", needReason: false },
    assign_owner: { title: "Assign owner code", needReason: false },
    retry: { title: "Retry conversion", needReason: false, plain: "Re-run the mineral adapter for this bundle now?" },
    retry_offer: { title: "Mapping saved", needReason: false, plain: "Owner code mapped. Retry this bundle's conversion now?" },
    extend_es: { title: "Extend due date", needReason: true },
    skip_es: { title: "Skip this period", needReason: true },
    link_doc: { title: "Link a document", needReason: false },
    resolve_error: { title: "Resolve error", needReason: false },
    reject_apu: { title: "Mark as not a value", needReason: false, plain: "Tell the system this isn't a financial fact (it's an identifier like an account number, or an OCR misread, or noise). Future statements from the same source won't surface this row again." },
  }[k];
  const reasonRequired = ["dispute_apu", "dispute_rec", "extend_es", "skip_es"].includes(k);
  const canSubmit = !busy &&
    (!reasonRequired || reason.trim().length > 0) &&
    (k !== "classify" || !!fact) &&
    (k !== "assign_owner" || (!!owner && legal.trim().length > 0)) &&
    (k !== "extend_es" || !!due) &&
    (k !== "link_doc" || !!docId);

  return (
    <Dialog open={!!modal} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid={`action-modal-${k}`}>
        <DialogHeader><DialogTitle className="font-serif text-xl">{cfg.title}</DialogTitle>
          <DialogDescription>{cfg.plain || "This action is recorded in the audit trail."}</DialogDescription></DialogHeader>

        {k === "classify" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Creates a corrected Claim that replaces this one. The original stays in the history for audit.</p>
            <Select value={fact} onValueChange={setFact}>
              <SelectTrigger data-testid="classify-fact-select"><SelectValue placeholder="Choose a fact type" /></SelectTrigger>
              <SelectContent>{factTypes.map((f) => <SelectItem key={f.name} value={f.name}>{f.description || prettyFact(f.name)}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Notes (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="classify-notes" />
          </div>
        )}
        {k === "assign_owner" && (
          <div className="space-y-3">
            <Row k="Owner code" v={entity?.data?.details?.owner_code} />
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger data-testid="assign-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
              <SelectContent><SelectItem value="david">David</SelectItem><SelectItem value="maryse">Maryse</SelectItem><SelectItem value="joint">Joint</SelectItem></SelectContent>
            </Select>
            <Input placeholder="Entity legal name (e.g. Five Dollar Shake LLC)" value={legal} onChange={(e) => setLegal(e.target.value)} data-testid="assign-owner-legal" />
            <Textarea placeholder="Notes (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          </div>
        )}
        {k === "extend_es" && (
          <div className="space-y-3">
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="extend-date" />
            <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="extend-reason" />
          </div>
        )}
        {k === "link_doc" && (
          <Select value={docId} onValueChange={setDocId}>
            <SelectTrigger data-testid="link-doc-select"><SelectValue placeholder="Choose a recent document" /></SelectTrigger>
            <SelectContent>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.original_filename || prettyFact(d.type)} · {fmtDate(d.received_at)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {cfg.needReason && k !== "classify" && k !== "extend_es" && (
          <Textarea placeholder={reasonRequired ? "Reason (required)" : "Reason (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="action-reason" />
        )}
        {(k === "resolve_error") && (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReason("dismissed without action")} data-testid="dismiss-prefill">Mark as dismissed without action</Button>
          </div>
        )}
        {k === "reject_apu" && (
          <div className="space-y-3">
            <Select value={rejectReason} onValueChange={setRejectReason}>
              <SelectTrigger data-testid="reject-reason-select"><SelectValue placeholder="Reason" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="not_a_value">Not a value (identifier)</SelectItem>
                <SelectItem value="ocr_error">OCR error</SelectItem>
                <SelectItem value="duplicate">Duplicate</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Notes (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="reject-notes" />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit[k]} disabled={!canSubmit} data-testid={`submit-${k}`}>{busy ? <Spinner size={16} className="animate-spin" /> : k === "reject_apu" ? "Reject this value" : "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// APU provenance body — top + sections + per-reconciliation action buttons.
const ApuBody = ({ prov, factLabels, openPdf, actions }) => {
  const { apu, source_documents = [], reconciliations = [], history = {}, ownership_provenance = {} } = prov;
  const src = apu.source || {};
  return (
    <div data-testid="drawer-apu">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-serif text-4xl tabular-nums" data-testid="drawer-apu-value">{formatAPUValue(apu.value, apu.unit_of_value, true)}</span>
        <Badge tone={trustTone(apu.trust_state)} testid="drawer-apu-trust">{trustLabel(apu.trust_state)}</Badge>
      </div>
      <p className="text-sm text-muted-foreground mt-1">{factLabel(apu.fact_type, factLabels)}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-muted border border-border">{fmtDate(apu.period?.start)} → {fmtDate(apu.period?.end)}</span>
        <span className="px-2 py-0.5 rounded-full bg-muted border border-border" data-testid="drawer-apu-ownership">owned by {apu.ownership?.owned_by || "—"}{apu.ownership?.decided_by ? ` · decided by ${apu.ownership.decided_by}` : " · not yet confirmed"}</span>
      </div>
      <div className="mt-4">
        <Section title="Source documents" count={source_documents.length} defaultOpen>
          {source_documents.map((d) => (
            <div key={d.id} className="text-sm border border-border rounded-lg p-3">
              <p className="font-semibold">{d.original_filename || prettyFact(d.type)}</p>
              <p className="text-xs text-muted-foreground">{d.source_kind} · {fmtDate(d.received_at)} · by {d.uploaded_by || "system"}</p>
              <p className="text-xs text-muted-foreground mt-1">{src.page != null ? `Page ${src.page}, ` : ""}Line/cell {src.line_or_cell || "—"} · {src.extraction_method}</p>
              <button onClick={() => openPdf(d.id, d.original_filename)} data-testid={`drawer-apu-view-pdf-${d.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--accent))] hover:underline"><FilePdf size={14} /> View document</button>
            </div>
          ))}
          {source_documents.length === 0 && <p className="text-sm text-muted-foreground">No source document linked.</p>}
        </Section>

        <Section title="Reconciliations" count={reconciliations.length} defaultOpen>
          {reconciliations.map((r) => (
            <div key={r.id} className="text-sm border border-border rounded-lg p-3" data-testid={`drawer-rec-${r.id}`}>
              <div className="flex items-center justify-between">
                <Badge tone={r.state === "confirmed" ? "success" : r.state === "rejected" ? "destructive" : "warning"}>{r.state}</Badge>
                {r.confidence_score != null && <span className="text-xs text-muted-foreground">confidence {(r.confidence_score * 100).toFixed(0)}%</span>}
              </div>
              {(r.counterpart_apus || []).map((c) => (
                <p key={c.id} className="text-xs text-muted-foreground mt-2">↔ <span className="font-semibold text-foreground tabular-nums">{formatAPUValue(c.value, c.unit_of_value, true)}</span> {factLabel(c.fact_type, factLabels)}</p>
              ))}
              {r.signals && Object.keys(r.signals).length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-muted-foreground">
                  {Object.entries(r.signals).map(([key, v]) => <span key={key}>{prettyFact(key)}: <span className="font-medium text-foreground">{typeof v === "number" ? v.toFixed(2) : String(v)}</span></span>)}
                </div>
              )}
              {r.state === "suggested" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" data-testid={`rec-confirm-${r.id}`} onClick={() => actions.confirmRec(r.id)}><Check size={14} className="mr-1" /> Confirm match</Button>
                  <Button size="sm" variant="outline" data-testid={`rec-reject-${r.id}`} onClick={() => actions.rejectRec(r.id)}><X size={14} className="mr-1" /> Reject</Button>
                </div>
              )}
              {r.state === "confirmed" && (
                <div className="mt-3"><Button size="sm" variant="outline" data-testid={`rec-dispute-${r.id}`} onClick={() => actions.disputeRec(r.id)}><Gavel size={14} className="mr-1" /> Dispute this match</Button></div>
              )}
            </div>
          ))}
          {reconciliations.length === 0 && <p className="text-sm text-muted-foreground">Waiting for a second source to agree.</p>}
        </Section>

        <Section title="History" count={(history.reconciliation_events || []).length}>
          {history.supersedes && <p className="text-xs text-muted-foreground">⤺ {history.supersedes.brief}</p>}
          {history.superseded_by && <p className="text-xs text-muted-foreground">⤻ {history.superseded_by.brief}</p>}
          {(history.reconciliation_events || []).map((e) => (
            <div key={e.id} className="text-sm border-b border-border/60 last:border-0 pb-2"><p className="font-medium">{eventLabel(e.event_type)}</p><p className="text-xs text-muted-foreground">{trustLabel(e.from_state)} → {trustLabel(e.to_state)} · {e.triggered_by}{e.decided_by ? ` (${e.decided_by})` : ""} · {fmtDate(e.decided_at)}</p>{e.notes && <p className="text-xs italic text-muted-foreground">{e.notes}</p>}</div>
          ))}
        </Section>

        <Section title="Who owns this">
          <Row k="Owned by" v={ownership_provenance.owned_by || "—"} />
          <Row k="Decided by" v={ownership_provenance.decided_by || "Not yet confirmed"} />
          <Row k="Decided at" v={fmtDate(ownership_provenance.decided_at)} />
        </Section>
      </div>
    </div>
  );
};
